import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { portalFetch } from '../lib/supabase';
import { printKitchenTicket } from '../components/KitchenTicket';

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];

function Card({ children, title, style }) {
  return (
    <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem', ...style }}>
      {title && <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.1rem', fontWeight: 700, marginBottom: '1.2rem', paddingBottom: '0.8rem', borderBottom: '1px solid var(--border)' }}>{title}</h2>}
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border)',
  borderRadius: 8, fontSize: '0.9rem', outline: 'none', color: 'var(--ink)', background: 'white',
};

const Toggle = ({ value, onChange }) => (
  <div
    onClick={() => onChange(!value)}
    style={{
      width: 44, height: 24, borderRadius: 12, cursor: 'pointer',
      background: value ? 'var(--orange)' : '#D1D5DB',
      position: 'relative', transition: 'background 0.2s', flexShrink: 0,
    }}
  >
    <div style={{
      position: 'absolute', top: 3, left: value ? 23 : 3,
      width: 18, height: 18, borderRadius: '50%', background: 'white',
      transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }} />
  </div>
);

export default function Settings() {
  const { restaurant, loadRestaurant } = useAuth();
  const [hours, setHours]     = useState({});
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [profile, setProfile] = useState({ phone: '', website: '', description: '', tax_rate: 6 });

  // Delivery settings state
  const [delivery, setDelivery]             = useState({ enabled: false, method: 'own_drivers' });
  const [deliverySaving, setDeliverySaving] = useState(false);
  const [deliverySaved, setDeliverySaved]   = useState(false);
  const [deliveryError, setDeliveryError]   = useState('');

  // Pause state
  const [pause, setPause]           = useState({ orders_paused: false, reservations_paused: false, pause_resume_at: '' });
  const [pauseSaving, setPauseSaving] = useState(false);
  const [pauseSaved, setPauseSaved]   = useState(false);

  // Billing / plan state
  const [billingInfo, setBillingInfo] = useState(null);

  // Printer settings state
  const [printer, setPrinter]           = useState({});
  const [printerSaving, setPrinterSaving] = useState(false);
  const [printerSaved, setPrinterSaved]   = useState(false);
  const [testPrinting, setTestPrinting]   = useState(false);

  // ── Branding state ───────────────────────────────────────────────────────────
  const [branding, setBranding] = useState({
    logo_image_url: '',
    hero_image_url: '',
    brand_color:    '#E85D04',
    tagline:        '',
    about_text:     '',
    website_url:    '',
    owner_photo_url: '',
  });
  const [brandingSaving, setBrandingSaving]   = useState(false);
  const [brandingSaved, setBrandingSaved]     = useState(false);
  const [brandingError, setBrandingError]     = useState('');
  const [logoUploading, setLogoUploading]     = useState(false);
  const [heroUploading, setHeroUploading]     = useState(false);
  const [logoPreview, setLogoPreview]         = useState(null);
  const [heroPreview, setHeroPreview]         = useState(null);
  const [ownerUploading, setOwnerUploading]   = useState(false);
  const [ownerPreview, setOwnerPreview]       = useState(null);

  // ── POS source state ──────────────────────────────────────────────────────────
  const [posSource, setPosSource]       = useState('');
  const [posSourceSaving, setPosSourceSaving] = useState(false);
  const [posSourceSaved, setPosSourceSaved]   = useState(false);

  // ── Clover POS state ──────────────────────────────────────────────────────────
  const [clover, setClover] = useState({
    enabled: false,
    merchant_id: '',
    access_token: '',
    has_token: false,
    connected_via_oauth: false,
  });
  const [cloverSaving, setCloverSaving]         = useState(false);
  const [cloverSaved, setCloverSaved]           = useState(false);
  const [cloverError, setCloverError]           = useState('');
  const [cloverTestLoading, setCloverTestLoading] = useState(false);
  const [cloverTestResult, setCloverTestResult]   = useState(null);
  const [cloverShowManual, setCloverShowManual]   = useState(false);

  // ── Square POS state ──────────────────────────────────────────────────────────
  const [square, setSquare]           = useState({ enabled: false, location_id: '', access_token: '', has_token: false, connected_via_oauth: false, merchant_id: '' });
  const [squareSaving, setSquareSaving] = useState(false);
  const [squareSaved, setSquareSaved]   = useState(false);
  const [squareError, setSquareError]   = useState('');
  const [squareTestLoading, setSquareTestLoading] = useState(false);
  const [squareTestResult, setSquareTestResult]   = useState(null);
  const [squareShowManual, setSquareShowManual]   = useState(false);

  useEffect(() => {
    if (!restaurant) return;
    setProfile({ phone: restaurant.phone || '', website: restaurant.website || '', description: restaurant.description || '', tax_rate: restaurant.taxRate ?? 6 });
    setPause({
      orders_paused:       restaurant.ordersPaused       ?? false,
      reservations_paused: restaurant.reservationsPaused ?? false,
      pause_resume_at:     restaurant.pauseResumeAt      ? new Date(restaurant.pauseResumeAt).toISOString().slice(0, 16) : '',
    });
    loadHours();
    portalFetch('/billing').then(data => {
      if (data?.billing) setBillingInfo(data.billing);
    });
    portalFetch('/printer-settings').then(data => {
      if (data) setPrinter(data.settings || {});
    });
    // Load branding
    setBranding({
      logo_image_url: restaurant.logo_image_url || '',
      hero_image_url: restaurant.hero_image_url || '',
      brand_color:    restaurant.brand_color    || '#E85D04',
      tagline:        restaurant.tagline        || '',
      about_text:     restaurant.about_text     || '',
      owner_photo_url: restaurant.owner_photo_url || '',
      website_url:    restaurant.website_url    || '',
    });
    if (restaurant.logo_image_url) setLogoPreview(restaurant.logo_image_url);
    if (restaurant.hero_image_url) setHeroPreview(restaurant.hero_image_url);
    setOwnerPreview(restaurant.owner_photo_url || null);
    // Load POS source
    if (restaurant.pos_source) setPosSource(restaurant.pos_source);
    // Load Clover credentials
    portalFetch('/settings?type=clover').then(data => {
      if (data?.clover) {
        setClover({
          enabled:              data.clover.clover_enabled       ?? false,
          merchant_id:          data.clover.clover_merchant_id   ?? '',
          access_token:         '',
          has_token:            data.clover.has_token            ?? false,
          connected_via_oauth:  data.clover.connected_via_oauth  ?? false,
        });
      }
    });
    // Check for OAuth callback result in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('clover_connected') === 'true') {
      setCloverTestResult({ ok: true, message: '✓ Clover connected successfully via OAuth!' });
      window.history.replaceState({}, '', window.location.pathname);
      // Reload clover state
      portalFetch('/settings?type=clover').then(data => {
        if (data?.clover) {
          setClover({
            enabled:             data.clover.clover_enabled      ?? false,
            merchant_id:         data.clover.clover_merchant_id  ?? '',
            access_token:        '',
            has_token:           data.clover.has_token           ?? false,
            connected_via_oauth: data.clover.connected_via_oauth ?? false,
          });
        }
      });
    }
    if (urlParams.get('clover_error')) {
      setCloverError(`OAuth error: ${urlParams.get('clover_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
    // Load Square credentials
    portalFetch('/settings?type=square').then(data => {
      if (data?.square) {
        setSquare({
          enabled:             data.square.square_enabled            ?? false,
          location_id:         data.square.square_location_id        ?? '',
          access_token:        '',
          has_token:           data.square.has_token                 ?? false,
          connected_via_oauth: data.square.connected_via_oauth       ?? false,
          merchant_id:         data.square.square_merchant_id        ?? '',
        });
      }
    });
    // Check for Square OAuth callback result in URL
    const urlParamsSquare = new URLSearchParams(window.location.search);
    if (urlParamsSquare.get('square_connected') === 'true') {
      setSquareTestResult({ ok: true, message: '✓ Square connected successfully via OAuth!' });
      window.history.replaceState({}, '', window.location.pathname);
      portalFetch('/settings?type=square').then(data => {
        if (data?.square) {
          setSquare({
            enabled:             data.square.square_enabled            ?? false,
            location_id:         data.square.square_location_id        ?? '',
            access_token:        '',
            has_token:           data.square.has_token                 ?? false,
            connected_via_oauth: data.square.connected_via_oauth       ?? false,
            merchant_id:         data.square.square_merchant_id        ?? '',
          });
        }
      });
    }
    if (urlParamsSquare.get('square_error')) {
      setSquareError(`OAuth error: ${urlParamsSquare.get('square_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [restaurant?.id]);

  const loadHours = async () => {
    const data = await portalFetch('/settings');
    if (data?.hours) {
      const map = {};
      data.hours.forEach(h => { map[h.day_of_week] = h; });
      setHours(map);
    }
    if (data?.delivery) {
      setDelivery({
        enabled:       data.delivery.enabled       ?? false,
        method:        data.delivery.method        ?? 'own_drivers',
        fee:           data.delivery.fee           ?? 0,

        radius_miles:  data.delivery.radius_miles  ?? 5,
      });
    }
  };

  // ── Branding helpers ─────────────────────────────────────────────────────────
  const uploadBrandingImage = async (file, field) => {
    if (!file) return;
    const isLogo = field === 'logo_image_url';
    isLogo ? setLogoUploading(true) : setHeroUploading(true);
    setBrandingError('');
    try {
      // Compress and convert to base64
      const reader = new FileReader();
      const base64 = await new Promise((res, rej) => {
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const result = await portalFetch('/branding-upload', {
        method: 'POST',
        body: JSON.stringify({
          imageBase64: base64,
          mimeType:    file.type,
          field,
        }),
      });
      if (result?.url) {
        setBranding(b => ({ ...b, [field]: result.url }));
        isLogo ? setLogoPreview(result.url) : setHeroPreview(result.url);
      } else {
        setBrandingError(result?.error || 'Upload failed. Please try again.');
      }
    } catch (err) {
      setBrandingError(err.message || 'Upload failed.');
    } finally {
      isLogo ? setLogoUploading(false) : setHeroUploading(false);
    }
  };

  const uploadOwnerPhoto = async (file) => {
    if (!file) return;
    setOwnerUploading(true);
    setBrandingError('');
    try {
      const reader = new FileReader();
      const base64 = await new Promise((res, rej) => {
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const result = await portalFetch('/branding-upload', {
        method: 'POST',
        body: JSON.stringify({ imageBase64: base64, mimeType: file.type, field: 'owner_photo_url' }),
      });
      if (result?.url) {
        setBranding(b => ({ ...b, owner_photo_url: result.url }));
        setOwnerPreview(result.url);
      } else {
        setBrandingError(result?.error || 'Upload failed. Please try again.');
      }
    } catch (err) {
      setBrandingError(err.message || 'Upload failed.');
    } finally {
      setOwnerUploading(false);
    }
  };

  const saveBranding = async () => {
    setBrandingSaving(true);
    setBrandingError('');
    const result = await portalFetch('/settings', {
      method: 'PATCH',
      body: JSON.stringify({ type: 'branding', updates: branding }),
    });
    setBrandingSaving(false);
    if (result?.ok) {
      await loadRestaurant();
      setBrandingSaved(true);
      setTimeout(() => setBrandingSaved(false), 3000);
    } else {
      setBrandingError(result?.error || 'Failed to save branding. Please try again.');
    }
  };

  const handleHourChange = (day, field, value) => {
    setHours(prev => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
  };

  const saveHours = async () => {
    setSaving(true);
    const updates = DAYS.map(day => {
      const h = hours[day] || {};
      return { day_of_week: day, is_closed: h.is_closed || false, open_time: h.is_closed ? null : (h.open_time || '11:00'), close_time: h.is_closed ? null : (h.close_time || '21:00') };
    });
    await portalFetch('/settings', { method: 'PATCH', body: JSON.stringify({ type: 'hours', updates }) });
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 3000);
  };

  const saveProfile = async () => {
    setSaving(true);
    await portalFetch('/settings', { method: 'PATCH', body: JSON.stringify({ type: 'profile', updates: profile }) });
    await loadRestaurant();
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 3000);
  };

  const saveDelivery = async () => {
    setDeliveryError('');

    const radius  = parseFloat(delivery.radius_miles);
    const fee     = parseFloat(delivery.fee ?? 0);
    const method = delivery.method || 'own_drivers';

    // radius and fee validation handled admin-side
    setDeliverySaving(true);
    const result = await portalFetch('/settings', {
      method: 'PATCH',
      body: JSON.stringify({ type: 'delivery', updates: { enabled: delivery.enabled, method } }),
    });
    setDeliverySaving(false);
    if (result?.delivery) { setDeliverySaved(true); setTimeout(() => setDeliverySaved(false), 3000); }
    else { setDeliveryError('Failed to save delivery settings. Please try again.'); }
  };

  const savePause = async () => {
    setPauseSaving(true);
    await portalFetch('/settings', { method: 'PATCH', body: JSON.stringify({ type: 'pause', updates: pause }) });
    await loadRestaurant();
    setPauseSaving(false); setPauseSaved(true); setTimeout(() => setPauseSaved(false), 3000);
  };

  const chooseTier = async (tier) => {
    const result = await portalFetch('/billing', {
      method: 'PATCH',
      body: JSON.stringify({ type: 'choose_tier', updates: { tier } }),
    });
    if (result?.billing) setBillingInfo(prev => ({ ...prev, ...result.billing }));
  };

  const handlePrinterSave = async () => {
    setPrinterSaving(true);
    await portalFetch('/printer-settings', { method: 'PATCH', body: JSON.stringify(printer) });
    setPrinterSaving(false); setPrinterSaved(true); setTimeout(() => setPrinterSaved(false), 3000);
  };

  const handleTestPrint = async () => {
    setTestPrinting(true);
    await portalFetch('/printer-settings', { method: 'POST', body: JSON.stringify({ action: 'test' }) });
    setTestPrinting(false);
  };

  // ── POS source save ───────────────────────────────────────────────────────────
  const savePosSource = async () => {
    setPosSourceSaving(true);
    await portalFetch('/settings', {
      method: 'PATCH',
      body: JSON.stringify({ type: 'pos_source', updates: { pos_source: posSource || null } }),
    });
    await loadRestaurant();
    setPosSourceSaving(false);
    setPosSourceSaved(true);
    setTimeout(() => setPosSourceSaved(false), 3000);
  };

  // ── Clover OAuth connect ──────────────────────────────────────────────────────
  const connectCloverOAuth = () => {
    if (!restaurant?.id) return;
    window.location.href = `https://app.tablechat-ai.com/api/clover/oauth-connect?restaurantId=${restaurant.id}`;
  };

  // ── Clover manual token save (fallback while OAuth sandbox issues persist) ────
  const saveCloverManual = async () => {
    setCloverError('');
    setCloverTestResult(null);

    if (!clover.merchant_id.trim()) {
      setCloverError('Merchant ID is required.');
      return;
    }
    if (!clover.access_token.trim() && !clover.has_token) {
      setCloverError('API token is required.');
      return;
    }

    setCloverSaving(true);
    const result = await portalFetch('/settings', {
      method: 'PATCH',
      body: JSON.stringify({
        type: 'clover',
        updates: {
          clover_enabled:      true,
          clover_merchant_id:  clover.merchant_id.trim(),
          clover_access_token: clover.access_token.trim() || undefined,
        },
      }),
    });
    setCloverSaving(false);

    if (result?.ok) {
      setClover(s => ({ ...s, enabled: true, has_token: true, access_token: '', connected_via_oauth: false }));
      setCloverSaved(true);
      setCloverShowManual(false);
      setTimeout(() => setCloverSaved(false), 3000);
    } else {
      setCloverError(result?.error || 'Failed to save Clover settings. Please try again.');
    }
  };

  // ── Clover enabled toggle save ────────────────────────────────────────────────
  const saveCloverEnabled = async (enabled) => {
    setCloverError('');
    setCloverSaving(true);
    const result = await portalFetch('/settings', {
      method: 'PATCH',
      body: JSON.stringify({
        type: 'clover',
        updates: { clover_enabled: enabled },
      }),
    });
    setCloverSaving(false);
    if (result?.ok) {
      setClover(s => ({ ...s, enabled }));
      setCloverSaved(true);
      setTimeout(() => setCloverSaved(false), 3000);
    } else {
      setCloverError(result?.error || 'Failed to update Clover settings.');
    }
  };

  // ── Clover disconnect ─────────────────────────────────────────────────────────
  const disconnectClover = async () => {
    if (!window.confirm('Disconnect Clover? Orders will no longer be sent to your POS.')) return;
    setCloverSaving(true);
    const result = await portalFetch('/settings', {
      method: 'PATCH',
      body: JSON.stringify({
        type: 'clover',
        updates: {
          clover_enabled:          false,
          clover_merchant_id:      null,
          clover_access_token:     null,
          clover_refresh_token:    null,
          clover_token_expires_at: null,
        },
      }),
    });
    setCloverSaving(false);
    if (result?.ok) {
      setClover({ enabled: false, merchant_id: '', has_token: false, connected_via_oauth: false });
      setCloverTestResult(null);
    } else {
      setCloverError(result?.error || 'Failed to disconnect Clover.');
    }
  };

  // ── Clover connection test ────────────────────────────────────────────────────
  const testCloverConnection = async () => {
    setCloverTestLoading(true);
    setCloverTestResult(null);
    const result = await portalFetch('/settings', {
      method: 'PATCH',
      body: JSON.stringify({ type: 'clover_test' }),
    });
    setCloverTestLoading(false);
    setCloverTestResult(result?.ok
      ? { ok: true,  message: `✓ Connected · Merchant: ${result.merchant_name || clover.merchant_id}` }
      : { ok: false, message: result?.error || 'Connection failed. Check your credentials.' }
    );
  };

  // ── Square OAuth connect ─────────────────────────────────────────────────────
  const connectSquareOAuth = () => {
    if (!restaurant?.id) return;
    window.location.href = `https://app.tablechat-ai.com/api/square/oauth-connect?restaurantId=${restaurant.id}`;
  };

  // ── Square disconnect ─────────────────────────────────────────────────────────
  const disconnectSquare = async () => {
    if (!window.confirm('Disconnect Square? Orders will no longer be sent to your Square POS.')) return;
    setSquareSaving(true);
    const result = await portalFetch('/settings', {
      method: 'PATCH',
      body: JSON.stringify({
        type: 'square',
        updates: {
          square_enabled:             false,
          square_location_id:         null,
          square_access_token:        null,
          square_refresh_token:       null,
          square_token_expires_at:    null,
          square_merchant_id:         null,
          square_connected_via_oauth: false,
        },
      }),
    });
    setSquareSaving(false);
    if (result?.ok) {
      setSquare({ enabled: false, location_id: '', access_token: '', has_token: false, connected_via_oauth: false, merchant_id: '' });
      setSquareTestResult(null);
    } else {
      setSquareError(result?.error || 'Failed to disconnect Square.');
    }
  };

  // ── Square save ───────────────────────────────────────────────────────────────
  const saveSquare = async () => {
    setSquareError('');
    setSquareTestResult(null);

    if (square.enabled) {
      if (!square.location_id.trim()) {
        setSquareError('Location ID is required when Square is enabled.');
        return;
      }
      if (!square.access_token.trim() && !square.has_token) {
        setSquareError('Access token is required when Square is enabled.');
        return;
      }
    }

    setSquareSaving(true);
    const result = await portalFetch('/settings', {
      method: 'PATCH',
      body: JSON.stringify({
        type: 'square',
        updates: {
          square_enabled:      square.enabled,
          square_location_id:  square.location_id.trim(),
          square_access_token: square.access_token.trim() || null,
        },
      }),
    });
    setSquareSaving(false);

    if (result?.ok) {
      setSquare(s => ({ ...s, has_token: true, access_token: '' }));
      setSquareSaved(true);
      setTimeout(() => setSquareSaved(false), 3000);
    } else {
      setSquareError(result?.error || 'Failed to save Square settings. Please try again.');
    }
  };

  // ── Square connection test ────────────────────────────────────────────────────
  const testSquareConnection = async () => {
    setSquareTestLoading(true);
    setSquareTestResult(null);
    const result = await portalFetch('/settings', {
      method: 'PATCH',
      body: JSON.stringify({ type: 'square_test' }),
    });
    setSquareTestLoading(false);
    setSquareTestResult(result?.ok
      ? { ok: true,  message: `✓ Connected · Location: ${result.location_name || square.location_id}` }
      : { ok: false, message: result?.error || 'Connection failed. Check your credentials.' }
    );
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.8rem', fontWeight: 900 }}>Settings</h1>
        <p style={{ color: 'var(--muted)', fontSize: '0.875rem', marginTop: 2 }}>{restaurant?.name}</p>
      </div>

      {/* ── RESTAURANT PROFILE ─────────────────────────────────────────────────── */}
      <Card title="Restaurant profile">
        {[['Phone number', 'phone', '(301) 555-0100'], ['Website', 'website', 'www.yourrestaurant.com']].map(([label, key, placeholder]) => (
          <div key={key} style={{ marginBottom: '1.1rem' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '0.35rem' }}>{label}</label>
            <input style={inputStyle} value={profile[key]} onChange={e => setProfile(p => ({ ...p, [key]: e.target.value }))} placeholder={placeholder} />
          </div>
        ))}
        <div style={{ marginBottom: '1.1rem' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '0.35rem' }}>Description</label>
          <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }} value={profile.description} onChange={e => setProfile(p => ({ ...p, description: e.target.value }))} placeholder="Tell customers about your restaurant..." />
        </div>
        <div style={{ marginBottom: '1.1rem' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '0.35rem' }}>Sales tax rate (%)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <input type="number" min="0" max="20" step="0.1" style={{ ...inputStyle, width: 100 }} value={profile.tax_rate} onChange={e => setProfile(p => ({ ...p, tax_rate: parseFloat(e.target.value) || 0 }))} placeholder="6" />
            <span style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>% — applied to order subtotal</span>
          </div>
          <p style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: '0.3rem' }}>MD: 6% · DC: 10% · VA: ~6–7%. Check with your accountant if unsure.</p>
        </div>
        <button onClick={saveProfile} disabled={saving} style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.8rem 1.8rem', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
          {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save profile'}
        </button>
      </Card>

      {/* ── OPENING HOURS ──────────────────────────────────────────────────────── */}
      <Card title="Opening hours">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
          {DAYS.map(day => {
            const h = hours[day] || {};
            const closed = h.is_closed || false;
            return (
              <div key={day} style={{ display: 'grid', gridTemplateColumns: '110px 1fr', alignItems: 'center', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <input type="checkbox" checked={closed} onChange={e => handleHourChange(day, 'is_closed', e.target.checked)} style={{ width: 14, height: 14, accentColor: 'var(--orange)' }} />
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: closed ? 'var(--muted)' : 'var(--ink)', textTransform: 'capitalize' }}>{day.slice(0, 3)}</span>
                  {closed && <span style={{ fontSize: '0.72rem', color: 'var(--red-tx)', background: 'var(--red-bg)', padding: '0.1rem 0.4rem', borderRadius: 10, fontWeight: 700 }}>Closed</span>}
                </div>
                {!closed ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <input type="time" value={h.open_time || '11:00'} onChange={e => handleHourChange(day, 'open_time', e.target.value)} style={{ ...inputStyle, width: 'auto', flex: 1 }} />
                    <span style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>to</span>
                    <input type="time" value={h.close_time || '21:00'} onChange={e => handleHourChange(day, 'close_time', e.target.value)} style={{ ...inputStyle, width: 'auto', flex: 1 }} />
                  </div>
                ) : (
                  <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Closed all day</div>
                )}
              </div>
            );
          })}
        </div>
        <button onClick={saveHours} disabled={saving} style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.8rem 1.8rem', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', marginTop: '1.5rem' }}>
          {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save hours'}
        </button>
      </Card>

      {/* ── DELIVERY SETTINGS ──────────────────────────────────────────────────── */}
      {false && (
      <Card title="Delivery settings">
        <p style={{ color: 'var(--muted)', fontSize: '0.83rem', marginBottom: '1.2rem', lineHeight: 1.5 }}>
          Enable delivery to allow customers to place delivery orders. Choose between TableChat Drivers (our gig driver network) or TableChat Partner (third-party delivery).
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.9rem 0', borderBottom: '1px solid var(--border)', marginBottom: '1.2rem' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Delivery enabled</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: 2 }}>Allow customers to place delivery orders</div>
          </div>
          <Toggle value={delivery.enabled} onChange={v => setDelivery(d => ({ ...d, enabled: v }))} />
        </div>
        <div style={{ marginBottom: '1.4rem', opacity: delivery.enabled ? 1 : 0.4, pointerEvents: delivery.enabled ? 'auto' : 'none' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '0.35rem' }}>Delivery mode</label>
          <select style={{ ...inputStyle, cursor: delivery.enabled ? 'pointer' : 'not-allowed' }} disabled={!delivery.enabled} value={delivery.method || 'own_drivers'} onChange={e => setDelivery(d => ({ ...d, method: e.target.value }))}>
            <option value="own_drivers">TableChat Drivers</option>
            <option value="delivery_partner">TableChat Partner</option>
          </select>
        </div>
        {deliveryError && <p style={{ color: 'var(--red-tx)', fontSize: '0.85rem', marginBottom: '1rem', background: 'var(--red-bg)', padding: '0.6rem 0.9rem', borderRadius: 6 }}>{deliveryError}</p>}
        <button onClick={saveDelivery} disabled={deliverySaving} style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.8rem 1.8rem', fontWeight: 700, fontSize: '0.9rem', cursor: deliverySaving ? 'default' : 'pointer', opacity: deliverySaving ? 0.7 : 1 }}>
          {deliverySaving ? 'Saving...' : deliverySaved ? '✓ Saved' : 'Save delivery settings'}
        </button>
      </Card>
      )}

      {/* ── SERVICE AVAILABILITY ───────────────────────────────────────────────── */}
      <Card title="Service availability">
        <p style={{ color: 'var(--muted)', fontSize: '0.83rem', marginBottom: '1.2rem', lineHeight: 1.5 }}>
          Temporarily pause online orders, reservations, or both. Customers can still browse your menu and view your restaurant.
        </p>
        {(pause.orders_paused || pause.reservations_paused) && (
          <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1.2rem', fontSize: '0.85rem', color: '#856404', fontWeight: 600 }}>
            ⚠️ {pause.orders_paused && pause.reservations_paused ? 'Orders and reservations are paused.' : pause.orders_paused ? 'Online orders are paused.' : 'Reservations are paused.'} Customers cannot place requests until you resume.
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.9rem 0', borderBottom: '1px solid var(--border)' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: pause.orders_paused ? 'var(--red-tx)' : 'var(--ink)' }}>{pause.orders_paused ? '⏸ Online orders paused' : 'Pause online orders'}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: 2 }}>Stop accepting new pickup and delivery orders</div>
          </div>
          <Toggle value={pause.orders_paused} onChange={v => setPause(p => ({ ...p, orders_paused: v }))} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.9rem 0', borderBottom: '1px solid var(--border)', marginBottom: '1.2rem' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: pause.reservations_paused ? 'var(--red-tx)' : 'var(--ink)' }}>{pause.reservations_paused ? '⏸ Reservations paused' : 'Pause reservations'}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: 2 }}>Stop accepting new table bookings</div>
          </div>
          <Toggle value={pause.reservations_paused} onChange={v => setPause(p => ({ ...p, reservations_paused: v }))} />
        </div>
        {(pause.orders_paused || pause.reservations_paused) && (
          <div style={{ marginBottom: '1.2rem' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '0.35rem' }}>Auto-resume date & time (optional)</label>
            <input type="datetime-local" style={inputStyle} value={pause.pause_resume_at} onChange={e => setPause(p => ({ ...p, pause_resume_at: e.target.value }))} />
            <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>Leave blank to resume manually.</p>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={savePause} disabled={pauseSaving} style={{ background: pause.orders_paused || pause.reservations_paused ? '#dc3545' : 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.8rem 1.8rem', fontWeight: 700, fontSize: '0.9rem', cursor: pauseSaving ? 'default' : 'pointer', opacity: pauseSaving ? 0.7 : 1 }}>
            {pauseSaving ? 'Saving...' : pauseSaved ? '✓ Saved' : 'Save availability'}
          </button>
          {(pause.orders_paused || pause.reservations_paused) && (
            <button onClick={() => setPause({ orders_paused: false, reservations_paused: false, pause_resume_at: '' })} style={{ background: 'var(--green-bg, #e8f5ee)', color: 'var(--green-tx, #2d7a4f)', border: 'none', borderRadius: 8, padding: '0.8rem 1.8rem', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
              ▶ Resume all services
            </button>
          )}
        </div>
      </Card>

      {/* ── PLAN & BILLING ─────────────────────────────────────────────────────── */}
      <Card title="Plan & billing">
        {!billingInfo ? (
          <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Loading plan info...</p>
        ) : !billingInfo.pricing ? (
          <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Plan details are unavailable right now. Please contact support@tablechat-ai.com.</p>
        ) : (() => {
          const cur = billingInfo.pricing.current || {};
          const nxt = billingInfo.pricing.next;
          const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';
          const isTrial = cur.kind === 'trial';
          const isPromo = cur.kind === 'promo';
          const planName = cur.plan_type === 'custom' ? 'Custom' : 'Standard';
          const detail = isTrial || isPromo
            ? 'No TableChat fees during this period'
            : cur.plan_type === 'custom'
              ? `${cur.fee_pct}% of the food subtotal on each order. No monthly fee.`
              : `$${cur.monthly_fee || billingInfo.standard_monthly_fee || 299} per month per location. No TableChat order fees.`;
          return (
            <>
              {(isTrial || isPromo) && (
                <div style={{ background: '#E6F1FB', border: '1px solid #B5D4F4', borderRadius: 8, padding: '0.9rem 1rem', marginBottom: '1.2rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0C447C', marginBottom: 3 }}>
                    {isTrial ? 'Free trial' : 'Promotional period'}: {cur.days_remaining} {cur.days_remaining === 1 ? 'day' : 'days'} remaining
                  </div>
                  <p style={{ fontSize: '0.8rem', color: '#185FA5', margin: 0, lineHeight: 1.5 }}>
                    Full access to all features. Ends {fmtD(cur.ends_at)}.
                  </p>
                </div>
              )}
              <div style={{ marginBottom: '1.2rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>
                  {isTrial || isPromo ? 'Current period' : 'Current plan'}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'Fraunces, serif', fontSize: '1.4rem', fontWeight: 900, color: 'var(--orange)' }}>
                    {isTrial ? 'Free trial' : isPromo ? 'Promotional rate' : planName}
                  </span>
                  <span style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>{detail}</span>
                </div>
              </div>
              {nxt && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.8rem 1rem', marginBottom: '1.2rem' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>
                    Starting {fmtD(nxt.starts_at)}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--ink)' }}>
                    <strong>{nxt.plan_type === 'custom' ? 'Custom' : 'Standard'}</strong>: {nxt.label}
                  </div>
                </div>
              )}
              {((cur.plan_type === 'standard' && !isTrial && !isPromo) || (nxt && nxt.plan_type === 'standard' && nxt.kind === 'regular')) && (() => {
                const pay = billingInfo.payment || {};
                return (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.8rem 1rem', marginBottom: '1.2rem' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Monthly payment</div>
                    <div style={{ fontSize: '0.9rem', color: 'var(--ink)', marginBottom: '0.6rem' }}>
                      {pay.on_file
                        ? `Bank account: ${pay.bank_name || 'Bank'} ending ${pay.bank_last4 || '----'}. Your plan fee is debited automatically each month.`
                        : 'Add a bank account so your monthly plan fee is paid automatically by bank debit (ACH). There is no extra charge.'}
                    </div>
                    {pay.failure_reason && (
                      <div style={{ fontSize: '0.8rem', color: '#791F1F', marginBottom: '0.6rem' }}>Your last payment did not go through: {pay.failure_reason}</div>
                    )}
                    <button
                      onClick={async () => {
                        try {
                          const r = await portalFetch('/payment-method', { method: 'POST' });
                          if (r && r.url) { window.location.href = r.url; }
                          else { alert((r && r.error) || 'Could not start bank account setup. Please try again.'); }
                        } catch (e) { alert('Could not start bank account setup. Please try again.'); }
                      }}
                      style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.6rem 1.2rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
                    >
                      {pay.on_file ? 'Change bank account' : 'Add bank account'}
                    </button>
                  </div>
                );
              })()}
              <p style={{ fontSize: '0.78rem', color: 'var(--muted)', margin: 0, lineHeight: 1.5 }}>
                Every feature is included on every plan. Credit card processing is paid by the customer. Questions about your plan? Contact support@tablechat-ai.com.
              </p>
            </>
          );
        })()}
      </Card>

      {/* ── ACCOUNT ────────────────────────────────────────────────────────────── */}
      <Card title="Account">
        <div style={{ fontSize: '0.875rem', color: 'var(--mid)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div><span style={{ color: 'var(--muted)' }}>Restaurant: </span><strong>{restaurant?.name}</strong></div>
          <div><span style={{ color: 'var(--muted)' }}>Status: </span><span style={{ color: restaurant?.active ? '#27500A' : '#791F1F', fontWeight: 700 }}>{restaurant?.active ? 'Active' : 'Inactive'}</span></div>
          <div><span style={{ color: 'var(--muted)' }}>Cuisine: </span>{restaurant?.cuisine}</div>
          <div><span style={{ color: 'var(--muted)' }}>Location: </span>{restaurant?.location_text}</div>
        </div>
        <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '1rem' }}>
          To update your restaurant name, location, or plan contact <a href="mailto:support@tablechat-ai.com" style={{ color: 'var(--orange)' }}>support@tablechat-ai.com</a>
        </p>
      </Card>

      {/* ── POS SOURCE ─────────────────────────────────────────────────────────── */}
      <Card title="POS Menu Source">
        <p style={{ color: 'var(--muted)', fontSize: '0.83rem', marginBottom: '1.2rem', lineHeight: 1.5 }}>
          Choose which POS system drives your TableChat menu. When set, TableChat will automatically
          sync your menu, prices, and modifier groups from your POS daily at 3 AM.
          You can also trigger a manual sync from the Menu page.
        </p>
        <div style={{ marginBottom: '1.2rem' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '0.35rem' }}>
            Menu source
          </label>
          <select
            style={{ ...inputStyle, cursor: 'pointer' }}
            value={posSource}
            onChange={e => setPosSource(e.target.value)}
          >
            <option value="">— TableChat managed (no auto-sync) —</option>
            <option value="square">Square</option>
            <option value="clover">Clover</option>
          </select>
          <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>
            Your POS integration must be enabled and configured below before selecting it here.
          </p>
        </div>
        {restaurant?.pos_last_synced_at && (
          <p style={{ fontSize: '0.78rem', color: 'var(--muted)', marginBottom: '1rem' }}>
            Last synced: {new Date(restaurant.pos_last_synced_at).toLocaleString()}
          </p>
        )}
        <button
          onClick={savePosSource}
          disabled={posSourceSaving}
          style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.8rem 1.8rem', fontWeight: 700, fontSize: '0.9rem', cursor: posSourceSaving ? 'default' : 'pointer', opacity: posSourceSaving ? 0.7 : 1 }}
        >
          {posSourceSaving ? 'Saving...' : posSourceSaved ? '✓ Saved' : 'Save POS source'}
        </button>
      </Card>

      {/* ── CLOVER POS ─────────────────────────────────────────────────────────── */}
      <Card title="Clover POS Integration">
        <p style={{ color: 'var(--muted)', fontSize: '0.83rem', marginBottom: '1.2rem', lineHeight: 1.5 }}>
          Connect your Clover account to automatically send TableChat orders to your Clover POS.
          Orders appear on your Clover kitchen display or terminal in real time — no second tablet needed.
        </p>

        {/* ── Connected state ── */}
        {clover.has_token ? (
          <>
            {/* Connected badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: '#E1F5EE', border: '1px solid #6EE7B7', borderRadius: 10, padding: '0.9rem 1.1rem', marginBottom: '1.4rem' }}>
              <span style={{ fontSize: '1.3rem' }}>✅</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0F6E56' }}>Clover connected</div>
                <div style={{ fontSize: '0.78rem', color: '#1a7a5e', marginTop: 2 }}>
                  {clover.merchant_id ? `Merchant ID: ${clover.merchant_id}` : 'Connected via OAuth'}
                  {clover.connected_via_oauth && ' · OAuth'}
                </div>
              </div>
              <button
                onClick={disconnectClover}
                disabled={cloverSaving}
                style={{ background: 'white', color: '#dc3545', border: '1.5px solid #dc3545', borderRadius: 8, padding: '0.5rem 1rem', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}
              >
                Disconnect
              </button>
            </div>

            {/* Enable/disable toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.9rem 0', borderBottom: '1px solid var(--border)', marginBottom: '1.2rem' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Send orders to Clover</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: 2 }}>Orders placed through TableChat will appear in your Clover POS</div>
              </div>
              <Toggle value={clover.enabled} onChange={v => saveCloverEnabled(v)} />
            </div>

            {cloverError && <p style={{ color: 'var(--red-tx)', fontSize: '0.85rem', marginBottom: '1rem', background: 'var(--red-bg)', padding: '0.6rem 0.9rem', borderRadius: 6 }}>{cloverError}</p>}
            {cloverSaved && <p style={{ color: 'var(--green-tx, #2d7a4f)', fontSize: '0.85rem', marginBottom: '1rem', fontWeight: 600 }}>✓ Saved</p>}

            {cloverTestResult && (
              <div style={{ fontSize: '0.85rem', marginBottom: '1rem', padding: '0.6rem 0.9rem', borderRadius: 6, background: cloverTestResult.ok ? 'var(--green-bg, #e8f5ee)' : 'var(--red-bg)', color: cloverTestResult.ok ? 'var(--green-tx, #2d7a4f)' : 'var(--red-tx)', fontWeight: 600 }}>
                {cloverTestResult.message}
              </div>
            )}

            <button
              onClick={testCloverConnection}
              disabled={cloverTestLoading}
              style={{ background: 'white', color: 'var(--dark)', border: '1.5px solid var(--border)', borderRadius: 8, padding: '0.8rem 1.4rem', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', opacity: cloverTestLoading ? 0.7 : 1 }}
            >
              {cloverTestLoading ? 'Testing...' : '🔌 Test connection'}
            </button>
          </>
        ) : (
          <>
            {/* ── Not connected state ── */}
            <div style={{ background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 10, padding: '1.2rem 1.4rem', marginBottom: '1.4rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.5rem' }}>🔗 Connect your Clover account</div>
              <p style={{ fontSize: '0.83rem', color: 'var(--mid)', lineHeight: 1.6, margin: 0 }}>
                Click the button below to securely connect your Clover merchant account.
                You'll be redirected to Clover to authorize TableChat, then brought back here automatically.
                No API tokens needed — Clover handles authentication.
              </p>
            </div>

            {cloverError && <p style={{ color: 'var(--red-tx)', fontSize: '0.85rem', marginBottom: '1rem', background: 'var(--red-bg)', padding: '0.6rem 0.9rem', borderRadius: 6 }}>{cloverError}</p>}

            <button
              onClick={connectCloverOAuth}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem', background: '#1DA462', color: 'white', border: 'none', borderRadius: 8, padding: '0.9rem 2rem', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', boxShadow: '0 2px 8px rgba(29,164,98,0.25)' }}
            >
              <span style={{ fontSize: '1.1rem' }}>🍀</span>
              Connect with Clover
            </button>

            <div style={{ marginTop: '1rem' }}>
              <button
                onClick={() => setCloverShowManual(s => !s)}
                style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: '0.8rem', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
              >
                {cloverShowManual ? 'Hide manual setup' : 'Having trouble? Enter API token manually'}
              </button>
            </div>

            {cloverShowManual && (
              <div style={{ marginTop: '1rem', padding: '1.1rem', background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 10 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem', marginBottom: '1.2rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '0.35rem' }}>Clover Merchant ID</label>
                    <input type="text" style={inputStyle} value={clover.merchant_id} onChange={e => setClover(s => ({ ...s, merchant_id: e.target.value }))} placeholder="e.g. KDA8GNSSF02F1" />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '0.35rem' }}>Clover API Token</label>
                    <input type="password" style={inputStyle} value={clover.access_token} onChange={e => setClover(s => ({ ...s, access_token: e.target.value }))} placeholder={clover.has_token ? '••••••••  (saved — leave blank to keep)' : 'Paste your Clover API token'} />
                    <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>
                      Generate this at Clover Merchant Dashboard → Settings → API Tokens → Create New Token. Check Inventory + Orders + Merchant Read permissions.
                    </p>
                  </div>
                </div>
                <button
                  onClick={saveCloverManual}
                  disabled={cloverSaving}
                  style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.7rem 1.5rem', fontWeight: 700, fontSize: '0.85rem', cursor: cloverSaving ? 'default' : 'pointer', opacity: cloverSaving ? 0.7 : 1 }}
                >
                  {cloverSaving ? 'Saving...' : 'Save manual token'}
                </button>
              </div>
            )}

            <div style={{ marginTop: '1.2rem', background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 8, padding: '0.8rem 1rem', fontSize: '0.8rem', color: '#795548', lineHeight: 1.6 }}>
              <strong>⚠️ Testing in sandbox?</strong> Make sure <strong>CLOVER_ENV=sandbox</strong> is set in your Vercel environment variables before connecting.
            </div>
          </>
        )}
      </Card>

      {/* ── SQUARE POS ─────────────────────────────────────────────────────────── */}
      <Card title="Square POS Integration">
        <p style={{ color: 'var(--muted)', fontSize: '0.83rem', marginBottom: '1.2rem', lineHeight: 1.5 }}>
          Connect your Square account to automatically send TableChat orders to your Square POS.
          Orders appear on your kitchen display or terminal in real time — no second tablet needed.
        </p>

        {square.has_token ? (
          <>
            {/* ── Connected state ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: '#E1F5EE', border: '1px solid #6EE7B7', borderRadius: 10, padding: '0.9rem 1.1rem', marginBottom: '1.4rem' }}>
              <span style={{ fontSize: '1.3rem' }}>✅</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0F6E56' }}>Square connected</div>
                <div style={{ fontSize: '0.78rem', color: '#1a7a5e', marginTop: 2 }}>
                  {square.connected_via_oauth
                    ? `Merchant ID: ${square.merchant_id || 'Connected'} · OAuth · Location: ${square.location_id}`
                    : `Location ID: ${square.location_id} · Manual token`}
                </div>
              </div>
              <button
                onClick={disconnectSquare}
                disabled={squareSaving}
                style={{ background: 'white', color: '#dc3545', border: '1.5px solid #dc3545', borderRadius: 8, padding: '0.5rem 1rem', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}
              >
                Disconnect
              </button>
            </div>

            {/* Enable/disable toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.9rem 0', borderBottom: '1px solid var(--border)', marginBottom: '1.2rem' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Send orders to Square</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: 2 }}>Orders placed through TableChat will appear in your Square POS</div>
              </div>
              <Toggle value={square.enabled} onChange={async v => {
                setSquare(s => ({ ...s, enabled: v }));
                await portalFetch('/settings', { method: 'PATCH', body: JSON.stringify({ type: 'square', updates: { square_enabled: v } }) });
              }} />
            </div>

            {squareError && <p style={{ color: 'var(--red-tx)', fontSize: '0.85rem', marginBottom: '1rem', background: 'var(--red-bg)', padding: '0.6rem 0.9rem', borderRadius: 6 }}>{squareError}</p>}
            {squareTestResult && (
              <div style={{ fontSize: '0.85rem', marginBottom: '1rem', padding: '0.6rem 0.9rem', borderRadius: 6, background: squareTestResult.ok ? 'var(--green-bg, #e8f5ee)' : 'var(--red-bg)', color: squareTestResult.ok ? 'var(--green-tx, #2d7a4f)' : 'var(--red-tx)', fontWeight: 600 }}>
                {squareTestResult.message}
              </div>
            )}

            <button
              onClick={testSquareConnection}
              disabled={squareTestLoading}
              style={{ background: 'white', color: 'var(--dark)', border: '1.5px solid var(--border)', borderRadius: 8, padding: '0.8rem 1.4rem', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', opacity: squareTestLoading ? 0.7 : 1 }}
            >
              {squareTestLoading ? 'Testing...' : '🔌 Test connection'}
            </button>
          </>
        ) : (
          <>
            {/* ── Not connected state ── */}
            <div style={{ background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 10, padding: '1.2rem 1.4rem', marginBottom: '1.4rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.5rem' }}>🔗 Connect your Square account</div>
              <p style={{ fontSize: '0.83rem', color: 'var(--mid)', lineHeight: 1.6, margin: 0 }}>
                Click the button below to securely connect your Square merchant account.
                You'll be redirected to Square to authorize TableChat, then brought back here automatically.
                TableChat will automatically register to receive order status updates from your Square POS.
              </p>
            </div>

            {squareError && <p style={{ color: 'var(--red-tx)', fontSize: '0.85rem', marginBottom: '1rem', background: 'var(--red-bg)', padding: '0.6rem 0.9rem', borderRadius: 6 }}>{squareError}</p>}
            {squareTestResult && (
              <div style={{ fontSize: '0.85rem', marginBottom: '1rem', padding: '0.6rem 0.9rem', borderRadius: 6, background: squareTestResult.ok ? 'var(--green-bg, #e8f5ee)' : 'var(--red-bg)', color: squareTestResult.ok ? 'var(--green-tx, #2d7a4f)' : 'var(--red-tx)', fontWeight: 600 }}>
                {squareTestResult.message}
              </div>
            )}

            <button
              onClick={connectSquareOAuth}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem', background: '#1DA462', color: 'white', border: 'none', borderRadius: 8, padding: '0.9rem 2rem', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', boxShadow: '0 2px 8px rgba(29,164,98,0.25)' }}
            >
              <span style={{ fontSize: '1.1rem' }}>🟩</span>
              Connect with Square
            </button>

            <div style={{ marginTop: '1rem' }}>
              <button
                onClick={() => setSquareShowManual(s => !s)}
                style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: '0.8rem', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
              >
                {squareShowManual ? 'Hide manual setup' : 'Having trouble? Enter credentials manually'}
              </button>
            </div>

            {squareShowManual && (
              <div style={{ marginTop: '1rem', padding: '1.1rem', background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 10 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem', marginBottom: '1.2rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '0.35rem' }}>Square Location ID</label>
                    <input type="text" style={inputStyle} value={square.location_id} onChange={e => setSquare(s => ({ ...s, location_id: e.target.value }))} placeholder="e.g. LG0YRMPB0X19A" />
                    <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>Found in your Square dashboard → Locations.</p>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '0.35rem' }}>Square Access Token</label>
                    <input type="password" style={inputStyle} value={square.access_token} onChange={e => setSquare(s => ({ ...s, access_token: e.target.value }))} placeholder={square.has_token ? '••••••••  (saved — leave blank to keep)' : 'Paste your Square access token'} />
                    <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>Found at developer.squareup.com → your app → Production → Access Token.</p>
                  </div>
                </div>
                <button
                  onClick={saveSquare}
                  disabled={squareSaving}
                  style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.7rem 1.5rem', fontWeight: 700, fontSize: '0.85rem', cursor: squareSaving ? 'default' : 'pointer', opacity: squareSaving ? 0.7 : 1 }}
                >
                  {squareSaving ? 'Saving...' : squareSaved ? '✓ Saved' : 'Save manual credentials'}
                </button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* ── BRANDING ──────────────────────────────────────────────────────────── */}
      <Card title="🎨 Restaurant branding">
        <p style={{ color: 'var(--muted)', fontSize: '0.83rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
          Customize how your restaurant appears to customers. Your logo, brand color, and tagline
          show on your TableChat page and PWA install screen.
        </p>

        {/* Logo upload */}
        <div style={{ marginBottom: '1.4rem' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '0.5rem' }}>
            Logo / Icon
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              width: 72, height: 72, borderRadius: 12, overflow: 'hidden', flexShrink: 0,
              background: `${branding.brand_color || '#E85D04'}15`,
              border: '1.5px dashed var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {logoPreview
                ? <img src={logoPreview} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} />
                : <span style={{ fontSize: '1.8rem', opacity: 0.4 }}>🍽️</span>
              }
            </div>
            <div>
              <label style={{
                display: 'inline-block', background: 'var(--orange)', color: 'white',
                padding: '0.5rem 1rem', borderRadius: 8, fontSize: '0.82rem', fontWeight: 700,
                cursor: logoUploading ? 'default' : 'pointer', opacity: logoUploading ? 0.7 : 1,
              }}>
                {logoUploading ? 'Uploading...' : logoPreview ? '🔄 Change logo' : '📤 Upload logo'}
                <input type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" style={{ display: 'none' }}
                  onChange={e => e.target.files[0] && uploadBrandingImage(e.target.files[0], 'logo_image_url')}
                  disabled={logoUploading}
                />
              </label>
              <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>
                PNG, JPG, WebP or SVG · Recommended: square, at least 192×192px
              </p>
            </div>
          </div>
        </div>

        {/* Hero image upload */}
        <div style={{ marginBottom: '1.4rem' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '0.5rem' }}>
            Hero / Cover image <span style={{ fontWeight: 400, color: 'var(--muted)' }}>(optional)</span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              width: 120, height: 60, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
              background: 'var(--cream)', border: '1.5px dashed var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {heroPreview
                ? <img src={heroPreview} alt="Hero" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span style={{ fontSize: '1.4rem', opacity: 0.3 }}>🌄</span>
              }
            </div>
            <div>
              <label style={{
                display: 'inline-block', background: 'white', color: 'var(--ink)',
                border: '1.5px solid var(--border)', padding: '0.5rem 1rem', borderRadius: 8,
                fontSize: '0.82rem', fontWeight: 600, cursor: heroUploading ? 'default' : 'pointer',
                opacity: heroUploading ? 0.7 : 1,
              }}>
                {heroUploading ? 'Uploading...' : heroPreview ? '🔄 Change hero' : '📤 Upload hero'}
                <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }}
                  onChange={e => e.target.files[0] && uploadBrandingImage(e.target.files[0], 'hero_image_url')}
                  disabled={heroUploading}
                />
              </label>
              <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>
                Wide banner image · Recommended: 1200×400px
              </p>
            </div>
          </div>
        </div>

        {/* Brand color */}
        <div style={{ marginBottom: '1.4rem' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '0.5rem' }}>
            Brand color
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <input
              type="color"
              value={branding.brand_color || '#E85D04'}
              onChange={e => setBranding(b => ({ ...b, brand_color: e.target.value }))}
              style={{ width: 48, height: 40, border: '1.5px solid var(--border)', borderRadius: 8, cursor: 'pointer', padding: 2 }}
            />
            <input
              type="text"
              value={branding.brand_color || '#E85D04'}
              onChange={e => setBranding(b => ({ ...b, brand_color: e.target.value }))}
              placeholder="#E85D04"
              style={{ ...inputStyle, width: 120 }}
            />
            <div style={{
              flex: 1, height: 40, borderRadius: 8,
              background: `linear-gradient(135deg, ${branding.brand_color || '#E85D04'}, ${branding.brand_color || '#E85D04'}99)`,
              border: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: 700, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>Preview</span>
            </div>
          </div>
          <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>
            Used for header accent, buttons, and PWA theme color on your restaurant page.
          </p>
        </div>

        {/* Tagline */}
        <div style={{ marginBottom: '1.1rem' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '0.35rem' }}>
            Tagline <span style={{ fontWeight: 400, color: 'var(--muted)' }}>(optional)</span>
          </label>
          <input
            type="text"
            value={branding.tagline}
            onChange={e => setBranding(b => ({ ...b, tagline: e.target.value }))}
            placeholder="e.g. Authentic West African flavors in Germantown"
            maxLength={100}
            style={inputStyle}
          />
          <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>
            {(branding.tagline || '').length}/100 characters
          </p>
        </div>

        {/* About text */}
        <div style={{ marginBottom: '1.1rem' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '0.35rem' }}>
            About <span style={{ fontWeight: 400, color: 'var(--muted)' }}>(optional)</span>
          </label>
          <textarea
            value={branding.about_text}
            onChange={e => setBranding(b => ({ ...b, about_text: e.target.value }))}
            placeholder="Tell customers your story — where you're from, what makes your food special..."
            rows={6}
            maxLength={1000}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
          />
          <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>
            {(branding.about_text || '').length}/1000 characters
          </p>
          <div style={{ marginTop: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: '0.5rem' }}>
              Your photo <span style={{ fontWeight: 400, color: 'var(--muted)' }}>(optional, shown beside your About text)</span>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{
                width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
                background: 'var(--cream)', border: '1.5px dashed var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {ownerPreview
                  ? <img src={ownerPreview} alt="Owner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: '1.6rem', opacity: 0.4 }}>{'\u{1F464}'}</span>
                }
              </div>
              <div>
                <label style={{
                  display: 'inline-block', background: 'var(--orange)', color: 'white',
                  padding: '0.5rem 1rem', borderRadius: 8, fontSize: '0.82rem', fontWeight: 700,
                  cursor: ownerUploading ? 'default' : 'pointer', opacity: ownerUploading ? 0.7 : 1,
                }}>
                  {ownerUploading ? 'Uploading...' : ownerPreview ? 'Change photo' : 'Upload your photo'}
                  <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }}
                    onChange={e => e.target.files[0] && uploadOwnerPhoto(e.target.files[0])}
                    disabled={ownerUploading}
                  />
                </label>
                {ownerPreview && (
                  <button type="button" onClick={() => { setBranding(b => ({ ...b, owner_photo_url: '' })); setOwnerPreview(null); }}
                    style={{ marginLeft: '0.6rem', background: 'none', border: 'none', color: 'var(--muted)', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline' }}>
                    Remove
                  </button>
                )}
                <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>
                  JPG, PNG or WebP. A square photo of your face works best. Click Save branding to publish.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Live preview */}
        {(logoPreview || branding.tagline || branding.brand_color) && (
          <div style={{ marginBottom: '1.4rem', padding: '1rem', background: 'var(--cream)', borderRadius: 10, border: '1px solid var(--border)' }}>
            <p style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem' }}>Header preview</p>
            <div style={{
              background: 'white', borderRadius: 8, padding: '0.75rem 1rem',
              borderBottom: `3px solid ${branding.brand_color || '#E85D04'}`,
              display: 'flex', alignItems: 'center', gap: '0.75rem',
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
                background: `${branding.brand_color || '#E85D04'}20`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {logoPreview
                  ? <img src={logoPreview} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 2 }} />
                  : <span style={{ fontSize: '1.2rem' }}>🍽️</span>
                }
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1A1A1A' }}>{restaurant?.name}</div>
                {branding.tagline && <div style={{ fontSize: '0.72rem', color: '#888' }}>{branding.tagline}</div>}
                <div style={{ fontSize: '0.72rem', color: '#aaa' }}>
                  Powered by <span style={{ color: branding.brand_color || '#E85D04', fontWeight: 600 }}>TableChat</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {brandingError && (
          <p style={{ color: 'var(--red-tx)', fontSize: '0.85rem', marginBottom: '1rem', background: 'var(--red-bg)', padding: '0.6rem 0.9rem', borderRadius: 6 }}>
            {brandingError}
          </p>
        )}

        <button
          onClick={saveBranding}
          disabled={brandingSaving}
          style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.8rem 1.8rem', fontWeight: 700, fontSize: '0.9rem', cursor: brandingSaving ? 'default' : 'pointer', opacity: brandingSaving ? 0.7 : 1 }}
        >
          {brandingSaving ? 'Saving...' : brandingSaved ? '✓ Saved' : 'Save branding'}
        </button>
      </Card>

      {/* -- BRANDED WEBSITE -- */}
      <Card title="Your branded website" style={{ marginTop: '1.5rem' }}>
        <p style={{ color: 'var(--muted)', fontSize: '0.83rem', marginBottom: '1.2rem', lineHeight: 1.5 }}>
          A dedicated website for {restaurant?.name}, built automatically from your branding, menu, and hours above. Share this link on Google, social media, or your own domain.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.9rem 1rem', background: 'var(--warm)', borderRadius: 8, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200, fontSize: '0.85rem', fontFamily: 'monospace', color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {restaurant?.slug}.tablechat-ai.com
          </div>
          <a href={`https://${restaurant?.slug}.tablechat-ai.com`} target="_blank" rel="noopener noreferrer"
            style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 6, padding: '0.5rem 1rem', fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none', whiteSpace: 'nowrap' }}>
            Preview →
          </a>
        </div>
      </Card>

      

      {/* ── KITCHEN PRINTER ────────────────────────────────────────────────────── */}
      <Card title="Kitchen Printer" style={{ marginTop: '1.5rem' }}>
        <p style={{ color: 'var(--muted)', fontSize: '0.83rem', marginBottom: '1.2rem', lineHeight: 1.5 }}>
          Connect a <strong>Star mC-Print3</strong> printer via <strong>StarIO.Online</strong> to automatically print kitchen tickets for every paid order and reservation. Your Access Identifier is found in the StarIO.Online dashboard under Devices after your printer connects.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.9rem 0', borderBottom: '1px solid var(--border)' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Cloud Auto-Print</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: 2 }}>Automatically print tickets via StarIO.Online on every paid order / reservation</div>
          </div>
          <Toggle value={printer?.cloud_print_enabled} onChange={v => setPrinter(p => ({ ...p, cloud_print_enabled: v }))} />
        </div>
        {[
          { key: 'auto_print_orders',       label: 'Auto-print Orders',       desc: 'Print a ticket for every paid order' },
          { key: 'auto_print_reservations', label: 'Auto-print Reservations', desc: 'Print a ticket for every new reservation' },
        ].map(({ key, label, desc }) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px solid var(--border)' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{label}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{desc}</div>
            </div>
            <Toggle value={printer?.[key] !== false} onChange={v => setPrinter(p => ({ ...p, [key]: v }))} />
          </div>
        ))}
        <div style={{ marginTop: '1.2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {[
            { key: 'star_printer_id', label: 'Access Identifier', placeholder: 'e.g. faf82755 (from StarIO.Online dashboard)', type: 'text' },
                        { key: 'printer_label',   label: 'Printer Label',      placeholder: 'e.g. Kitchen Printer', type: 'text' },
          ].map(({ key, label, placeholder, type }) => (
            <div key={key}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--mid)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
              <input type={type} value={printer?.[key] || ''} placeholder={placeholder} onChange={e => setPrinter(p => ({ ...p, [key]: e.target.value }))} style={{ width: '100%', padding: '0.65rem 0.9rem', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.2rem', alignItems: 'center' }}>
          <button onClick={handlePrinterSave} disabled={printerSaving} style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.65rem 1.4rem', fontWeight: 700, fontSize: '0.875rem', cursor: printerSaving ? 'default' : 'pointer', opacity: printerSaving ? 0.7 : 1 }}>
            {printerSaving ? 'Saving...' : 'Save Printer Settings'}
          </button>
          <button onClick={handleTestPrint} disabled={testPrinting} style={{ background: 'white', color: 'var(--dark)', border: '1.5px solid var(--border)', borderRadius: 8, padding: '0.65rem 1.2rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>
            {testPrinting ? 'Printing...' : '🖨️ Test Print'}
          </button>
          {printerSaved && <span style={{ color: 'var(--green-tx)', fontWeight: 600, fontSize: '0.875rem' }}>✓ Saved</span>}
        </div>
      </Card>
    </div>
  );
}
